'use client';

import { useState, useEffect, useRef } from 'react';
import { TourLauncher } from '@/src/components/onboarding/tour-launcher';
import { CalendarMiniView } from '@/src/components/ui-kontora/calendar-mini-view';
import { AvailabilityIndicator } from '@/src/components/team/availability-indicator';
import { VoiceCommandTrigger } from '@/src/components/ui-kontora/voice-command-trigger';
import type { NavigationContext } from '@/src/lib/navigation';

export interface OverflowMenuProps {
  activeContext: NavigationContext;
  availableContexts: readonly NavigationContext[];
  canUsePowerMode: boolean;
  userDisplayName: string;
  userPermissions: readonly string[];
  currentUserId: string;
}

export function OverflowMenu({
  activeContext,
  availableContexts,
  canUsePowerMode,
  userDisplayName,
  userPermissions,
  currentUserId,
}: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative hidden md:block">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Weitere Werkzeuge"
        className="inline-flex h-10 items-center gap-1.5 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2.5 text-[var(--k-color-text-secondary)] shadow-[var(--k-shadow-xs)] transition hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="5" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="19" cy="12" r="1.5" />
        </svg>
        <span className="hidden text-[11px] font-semibold uppercase tracking-wide lg:inline">Mehr</span>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Weitere Werkzeuge"
          className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]"
        >
          <div className="border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
              Werkzeuge
            </p>
          </div>

          <div className="p-2">
            <div className="flex items-center justify-between gap-2 rounded-[var(--k-radius-sm)] px-2.5 py-2 text-sm">
              <span className="text-[var(--k-color-text-secondary)]">Kalender</span>
              <CalendarMiniView />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-[var(--k-radius-sm)] px-2.5 py-2 text-sm">
              <span className="text-[var(--k-color-text-secondary)]">Verfügbarkeit</span>
              <AvailabilityIndicator currentUserId={currentUserId} />
            </div>

            <div className="my-1 border-t border-[var(--k-color-border-subtle)]" />

            <MenuRow label="Hilfe & Touren">
              <TourLauncher
                activeContext={activeContext}
                availableContexts={availableContexts}
                userDisplayName={userDisplayName}
                userPermissions={userPermissions}
              />
            </MenuRow>

            <MenuRow label="Spracheingabe">
              <VoiceCommandTrigger />
            </MenuRow>

            {canUsePowerMode ? (
              <p className="mt-2 rounded-md bg-[var(--k-color-area-system-soft)] px-2 py-1 text-[10px] text-[var(--k-color-text-muted)]">
                Power-Mode ist aktiv. Wechsel zurück im Mandanten-Menü.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-[var(--k-radius-sm)] px-2.5 py-2 text-sm transition hover:bg-[var(--k-color-surface-muted)]">
      <span className="text-[var(--k-color-text-secondary)]">{label}</span>
      <span className="flex items-center">{children}</span>
    </div>
  );
}